import React, { useState, useEffect } from "react";
import CustomListing from "../components/CustomListing";
import { useOrganizationApi } from "../api/organization";
import "../styles/layout.css";
import ReusableHeader from "../components/ReusableHeader";
import CustomButton from "../components/CustomButton";
import { useNavigate } from "react-router-dom";
import { decodeJwt } from "../util/decodeJwt";
import { toast } from "react-toastify";
import ReusableFilter from "../components/ReusableFilter";
import { Filter, Pencil, Trash } from "lucide-react";

export default function OrganizationManagement() {
   const {  deleteVendorOrg, getAllVendorOrg  } = useOrganizationApi()
  const navigate = useNavigate();
  const [userId, setUserId] = useState(""); // start with empty
  const [userRole, setUserRole] = useState(""); // store user role
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");

  // ✅ Filter state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterObject, setFilterObject] = useState({}); // ✅ store filters
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = decodeJwt(token);
        if (decoded?._id) {
          setUserId(decoded._id);
          if (decoded?.role) setUserRole(decoded.role); // store role
        }
      } catch (err) {
        console.error("JWT decode failed", err);
      }
    }
  }, []);

  const handleDelete = async (id) => {
    if (!userId) {
      toast.error("Unauthorized: User not found");
      return;
    }

    if (window.confirm("Are you sure you want to delete this Org?")) {
      try {
      
        await deleteVendorOrg(id, userId); // call API
        toast.success("Org deleted successfully");

        // ✅ reload or trigger refresh in CustomListing
        window.location.reload();
      } catch (err) {
        toast.error(err.message || "Failed to delete Org");
      }
    }
  };

  const statusStyles = {
    active: { color: "#ffffffff", backgroundColor: "var(--primary-color)" },
    inactive: { color: "#b71c1c", backgroundColor: "#ffcdd2" },
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "type", label: "Type" },
    { key: "gst", label: "GST" },
    { key: "email", label: "Email" },
    { key: "phoneNumber", label: "Phone" },
    { key: "address", label: "Address" },
    {
      key: "status",
      label: "Status",
      render: (row) => {
        const style = statusStyles[row.status] || {
          color: "#000",
          backgroundColor: "#fff",
        };
        return (
          <div
            style={{
              color: style.color,
              backgroundColor: style.backgroundColor,
              padding: "4px 8px",
              borderRadius: "4px",
              textAlign: "center",
            }}
          >
            {row.status}
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        debugger
        // ✅ Hide buttons if user's role doesn't allow OR some condition on row
        const canEditDelete =  userRole !== row.type
        if (!canEditDelete) return null;

        return (
          <div className="action-buttons">
            {/* Edit button */}
            <CustomButton
              label={<Pencil size={16} />}
              className="edit-btn"
              onClick={() => navigate(`/organizations/edit/${row._id}`)}
            />

            {/* Delete button */}
            <CustomButton
              label={<Trash size={16} />}
              className="delete-btn"
              onClick={() => handleDelete(row._id)}
            />
          </div>
        );
      },
    }
  ];

    useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (userId) {
        fetchWithFilters({ limit: 10, offset: 0 }).then(res => {
          if (res?.data) setRows(res.data); // 🔑 update table rows
        });
      }
    }, 500); // 0.5s debounce

    return () => clearTimeout(delayDebounce);
  }, [searchText, filterObject, userId]);


  const renderActionButtons = () => (
    <div className="header-buttons" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {/* 🔍 Search field */}
      <div className="header-search">
        <input
          type="text"
          className="search-input"
          placeholder="Search organizations..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      {/* Buttons */}
      <CustomButton
        className="header-btn"
        label="New Organization"
        onClick={() => navigate("/organizations/create")}
      />
    </div>
  );


    // ✅ Wrap API call to include filters
    const fetchWithFilters = async (params) => {
      return await getAllVendorOrg({
        ...params,
        ...filterObject,
        search: searchText || undefined, // 🔑 pass search to API
      });
    };

  
    // ✅ Apply filters
    const handleApplyFilters = () => {
      const newFilters = {};
      if (fromDate) newFilters.fromDate = fromDate;
      if (toDate) newFilters.toDate = toDate;
  
      setFilterObject(newFilters);
      setIsFilterOpen(false);
      toast.success("Filters applied");
    };
  
    // ✅ Remove filter
    const handleRemoveFilter = (key) => {
      const updated = { ...filterObject };
      delete updated[key];
      setFilterObject(updated);
  
      if (key === "fromDate") setFromDate("");
      if (key === "toDate") setToDate("");
    };
  
      // ✅ Convert filterObject to activeFilters array
    const activeFilters = Object.entries(filterObject).map(([key, value]) => {
      let label = key;
      if (key === "fromDate") label = "From";
      if (key === "toDate") label = "To";
      return { key, label, value };
    });
  
  return (
    <div className="inventoryManagement">
      <ReusableHeader
        title="Organization Management"
        children={renderActionButtons()}
      />

          {/* ✅ Active Filters Chips */}
      <div className="filter-chips-container">
        {activeFilters.map((filter, index) => (
          <div key={index} className="filter-chip">
            {filter.label}: {filter.value}
            <span
              className="remove-icon"
              onClick={() => handleRemoveFilter(filter.key)}
            >
              ✕
            </span>
          </div>
        ))}
      </div>

      {userId ? ( // ✅ only render CustomListing when userId is ready
        <CustomListing
         showCheckbox={false}   // ✅ THIS HIDES CHECKBOX
         data={rows}         // controlled rows
          setData={setRows}   // setter to update table instantly
          fetchData={fetchWithFilters}
          columns={columns}
          pageSize={10}
          postData={{ userId }}
        />
      ) : (
        <div style={{ padding: "20px", textAlign: "center" }}>
          Loading organizations...
        </div>
      )}
      {/* ✅ ReusableFilter for date inputs */}
      <ReusableFilter isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter Devices">
        <div className="filter-fields">
          <div className="filter-row">
            <label>From Date</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>

          <div className="filter-row">
            <label>To Date</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>

          <div className="filter-actions">
            <CustomButton className="apply-btn" label="Apply" onClick={handleApplyFilters} />
            <CustomButton
              className="clear-btn"
              label="Clear"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setFilterObject({});
                setIsFilterOpen(false);
              }}
            />
          </div>
        </div>
      </ReusableFilter>
    </div>
  );
}
